import moment from 'moment';

import {
  NgbActiveModal,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import {
  Component,
  Input,
  OnInit
} from '@angular/core';

import { BookingDiaryService }       from './booking-diary.service';
import { BookingDiaryMechanicModel } from './typings/booking-diary.mechanic';
import { ConfirmService }            from '../messages/confirm/confirm.service';
import { DropdownOptionModel }       from '../shared/components/dropdown/typings/dropdown-option';
import { FieldErrorsModel }          from '../messages/typings/field-errors';
import { hasNewVehicleName }         from '../shared/controls/new-vehicle-fields/new-vehicle-name';
import { MessagesService }           from '../messages/messages.service';
import { ProperCasePipe }            from '../shared/pipes/proper-case.pipe';

import {
  BookingDiaryBookingDraftModel,
  BookingDiaryNewCustomerModel,
  BookingDiaryNewVehicleModel
} from './typings/booking-diary.draft';

import {
  BookingDiaryCustomerModel,
  BookingDiarySearchResultModel,
  BookingDiaryVehicleModel,
  BookingDiaryVehicleSearchModel
} from './typings/booking-diary.customer';

import {
  DATE_FORMAT,
  formatClockLabel,
  MIN_HOURS,
  SLOT_MINUTES
} from './booking-diary.layout';

import {
  mechanicOptionsFor,
  NEW_VEHICLE_ID,
  onFileMessageKey,
  timeOptionsBetween,
  trimmedFields,
  vehicleOptionsFor
} from './booking-diary.options';

const SEARCH_MAX_ROWS  : number = 8;
const SEARCH_MIN_CHARS : number = 2;

// A booking taken over the phone: customer, vehicle, job, length and start, saved onto the slot
@Component({
  selector   : 'wor-booking-diary-booking-modal',
  template: require('./booking-diary.booking-modal.component.html')
})
export class BookingDiaryBookingModalComponent implements OnInit {
  @Input() date              : string;

  // the diary holds the day's bookings, so it answers whether a time is free
  @Input() isSlotFree        : ( mechanicId : string, startMinutes : number, endMinutes : number ) => boolean;
  @Input() mechanicId        : string;
  @Input() mechanics         : Array<BookingDiaryMechanicModel> = [];
  @Input() shopClosesMinutes : number;
  @Input() shopOpensMinutes  : number;
  @Input() startMinutes      : number;

  customer          : BookingDiaryCustomerModel       = null;
  customerQuery     : string                          = '';
  description       : string                          = '';
  error             : string                          = null;
  hasSearched       : boolean                         = false;
  hours             : number                          = 1;
  isLoadingVehicles : boolean                         = false;
  isNewCustomer     : boolean                         = false;
  isSaving         : boolean                         = false;
  mechanicOptions   : Array<DropdownOptionModel>      = [];
  minHours          : number                          = MIN_HOURS;
  resultCount       : number                          = 0;
  suggestedStart    : number                          = null;
  timeOptions       : Array<DropdownOptionModel>      = [];
  vehicleId         : string                          = null;
  vehicleOptions    : Array<DropdownOptionModel>      = [];
  vehicles          : Array<BookingDiaryVehicleModel> = [];

  // the day the diary opened the modal for, the one day its grid can vouch for
  private openingDate : string = '';

  newCustomer : BookingDiaryNewCustomerModel = {
    company_name: '',
    first_name  : '',
    last_name   : '',
    mobile      : ''
  };

  newVehicle : BookingDiaryNewVehicleModel = {
    color       : '',
    make        : '',
    model       : '',
    plate_number: ''
  };

  // a booking cannot be made into a day already gone
  isPastDay = ( day : NgbDateStruct ) : boolean => {
    const date = moment({ date: day.day, month: day.month - 1, year: day.year });

    return date.format(DATE_FORMAT) < moment().format(DATE_FORMAT);
  };

  // customers and vehicles are searched together, so a rego finds the customer as readily as a name
  searchFn = ( typed : string ) : Promise<Array<BookingDiarySearchResultModel>> => {
    const query = ( typed || '' ).trim();

    if ( query.length < SEARCH_MIN_CHARS ) {
      return Promise.resolve([]);
    }

    return Promise.all([
      this.bookingDiaryService.searchCustomers(query),
      this.bookingDiaryService.searchVehicles(query)
    ])
      .then(([ customers, vehicles ]) => [
        ...customers.slice(0, SEARCH_MAX_ROWS).map(( customer, index ) => ( {
          customer,
          heading: index === 0 && vehicles.length
            ? 'CUSTOMER_SPACE.CUSTOMERS'
            : undefined,
          label  : customer.display_name,
          meta   : [ customer.phone, customer.suburb ].filter(part => !!part).join(' · ')
        } )),
        ...vehicles.slice(0, SEARCH_MAX_ROWS).map(( vehicle, index ) => ( {
          heading: index === 0
            ? 'VEHICLE_SPACE.VEHICLES'
            : undefined,
          label  : [ vehicle.make, vehicle.model ].filter(part => !!part).join(' '),
          meta   : vehicle.display_name || '',
          plate  : vehicle.plate_number,
          vehicle
        } ))
      ])
      .catch(() => []);
  }

  // a vehicle chosen from the search is applied once its owner's vehicles have loaded
  private pendingVehicleId : string = null;

  constructor (
    public activeModal          : NgbActiveModal,
    private bookingDiaryService : BookingDiaryService,
    private confirmService      : ConfirmService,
    private messagesService     : MessagesService,
    private properCasePipe      : ProperCasePipe,
    private translateService    : TranslateService
  ) {}

  ngOnInit () : void {
    this.openingDate = this.date;

    this.mechanicOptions = mechanicOptionsFor(this.mechanics, this.translateService);

    this.buildVehicleOptions();
    this.refreshTimeOptions();
  }

  cancelNewCustomer () : void {
    this.isNewCustomer = false;
    this.vehicleId     = null;

    this.buildVehicleOptions();
    this.clearError();
  }

  get canSave () : boolean {
    return this.hasCustomer
      && !!this.date
      && this.hours >= this.minHours
      && !this.isNewVehicleUnnamed
      && !this.isSaving;
  }

  clearCustomer () : void {
    this.customer      = null;
    this.customerQuery = '';
    this.hasSearched   = false;
    this.resultCount   = 0;
    this.vehicleId     = null;
    this.vehicles      = [];

    this.buildVehicleOptions();
    this.clearError();
  }

  clearError () : void {
    this.error          = null;
    this.suggestedStart = null;
  }

  get customerSummary () : string {
    return [ this.customer && this.customer.phone, this.vehicleSummary ].filter(part => !!part).join(' · ');
  }

  get dateLabel () : string {
    return this.date
      ? moment(this.date, DATE_FORMAT).format('dddd, D MMMM YYYY')
      : this.translateService.instant('BOOKING_SPACE.DIARY.CHOOSE_DATE');
  }

  get durationMinutes () : number {
    return Math.round(( this.hours || 0 ) * 60);
  }

  get endMinutes () : number {
    return this.startMinutes + this.durationMinutes;
  }

  get hasCustomer () : boolean {
    return this.isNewCustomer
      ? this.isNewCustomerValid
      : !!this.customer;
  }

  get hasCustomerContext () : boolean {
    return !!this.customer || this.isNewCustomer;
  }

  // moved off the opening day, the diary's grid knows nothing of the day; the save asks the server
  get hasGridForDate () : boolean {
    return !!this.isSlotFree && !!this.date && this.date === this.openingDate;
  }

  get hasNoMatches () : boolean {
    return this.hasSearched && !this.resultCount && ( this.customerQuery || '' ).trim().length >= SEARCH_MIN_CHARS;
  }

  get isNewCustomerValid () : boolean {
    const typed = trimmedFields(this.newCustomer);

    return !!typed.company_name || ( !!typed.first_name && !!typed.last_name );
  }

  get isNewVehicle () : boolean {
    return this.vehicleId === NEW_VEHICLE_ID;
  }

  // details typed without a rego, make or model would add a vehicle no list can name
  get isNewVehicleUnnamed () : boolean {
    const typed = trimmedFields(this.newVehicle);

    return this.isNewVehicle
      && Object.keys(typed).some(key => !!typed[key])
      && !hasNewVehicleName(typed);
  }

  get mechanicName () : string {
    const mechanic = this.mechanics.find(item => item.id === this.mechanicId);

    return mechanic
      ? mechanic.name
      : this.translateService.instant('JS_SPACE.BOOKINGS.UNASSIGNED');
  }

  onHoursChange () : void {
    this.clearError();
    this.refreshTimeOptions();
  }

  onResults ( results : Array<BookingDiarySearchResultModel> ) : void {
    this.hasSearched = true;
    this.resultCount = ( results || [] ).length;
  }

  onVehicleChange ( value : string | number ) : void {
    this.vehicleId = value === null || value === undefined
      ? null
      : String(value);

    this.clearError();
  }

  // a booking past closing or into another is refused, and the next start that fits is offered
  save () : void {
    this.clearError();

    if ( !this.canSave ) {
      return;
    }

    if ( this.endMinutes > this.shopClosesMinutes ) {
      this.error          = this.translateService.instant('BOOKING_SPACE.DIARY.PAST_CLOSING');
      this.suggestedStart = this.nextFreeStart();

      return;
    }

    if ( this.hasGridForDate && !this.isSlotFree(this.mechanicId, this.startMinutes, this.endMinutes) ) {
      this.error          = this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_TAKEN');
      this.suggestedStart = this.nextFreeStart();

      return;
    }

    this.isSaving = true;

    this.isSlotStillFree()
      .then(isFree => isFree
        ? this.confirmTypedRecords()
        : Promise.reject(this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_TAKEN')))
      .then(isConfirmed => {
        if ( !isConfirmed ) {
          this.isSaving = false;

          return;
        }

        return this.createTypedRecords()
          .then(() => {
            const draft = this.buildDraft();

            return this.bookingDiaryService.createBooking(draft)
              .then(() => {
                this.activeModal.close(draft);
              });
          });
      })
      .catch(err => {
        this.error    = this.errorMessage(err);
        this.isSaving = false;
      });
  }

  get saveHint () : string {
    if ( !this.hasCustomer ) {
      return this.translateService.instant(this.isNewCustomer
        ? 'BOOKING_SPACE.DIARY.ENTER_NAME'
        : 'BOOKING_SPACE.DIARY.CHOOSE_CUSTOMER_FIRST');
    }

    if ( !this.date ) {
      return this.translateService.instant('BOOKING_SPACE.DIARY.CHOOSE_DATE');
    }

    if ( this.isNewVehicleUnnamed ) {
      return this.translateService.instant('BOOKING_SPACE.DIARY.NEW_VEHICLE_DETAILS');
    }

    return this.hours >= this.minHours
      ? ''
      : this.translateService.instant('BOOKING_SPACE.DIARY.ENTER_HOURS');
  }

  selectCustomer ( customer : BookingDiaryCustomerModel ) : void {
    this.customer      = customer;
    this.customerQuery = '';
    this.isNewCustomer = false;
    this.vehicleId     = null;
    this.vehicles      = [];

    this.buildVehicleOptions();
    this.clearError();
    this.loadVehicles(customer);
  }

  selectResult ( result : BookingDiarySearchResultModel ) : void {
    if ( result.customer ) {
      this.selectCustomer(result.customer);

      return;
    }

    if ( result.vehicle ) {
      this.selectVehicleResult(result.vehicle);
    }
  }

  // a rego hit picks the owner and, once their vehicles arrive, the vehicle itself
  selectVehicleResult ( vehicle : BookingDiaryVehicleSearchModel ) : void {
    this.pendingVehicleId = vehicle.id;

    this.selectCustomer({
      display_name: vehicle.display_name || '',
      id          : vehicle.customer_id
    });
  }

  // whatever was typed into the search is the best guess at the name, so it seeds first and last
  startNewCustomer () : void {
    const words = ( this.customerQuery || '' ).trim().split(/\s+/).filter(word => !!word);

    this.newCustomer.first_name = words[0] || '';
    this.newCustomer.last_name  = words.slice(1).join(' ');

    this.isNewCustomer = true;
    this.vehicleId     = null;
    this.vehicles      = [];

    this.buildVehicleOptions();
    this.clearError();
  }

  get suggestedStartLabel () : string {
    return formatClockLabel(this.suggestedStart);
  }

  get suggestionDetail () : string {
    return this.translateService.instant('BOOKING_SPACE.DIARY.IS_FREE_AT', {
      mechanic: this.mechanicName,
      time    : `${ formatClockLabel(this.suggestedStart) } – ${ formatClockLabel(this.suggestedStart + this.durationMinutes) }`
    });
  }

  get summaryLabel () : string {
    return this.hours > 0
      ? `${ formatClockLabel(this.startMinutes) } – ${ formatClockLabel(this.endMinutes) } · ${ this.mechanicName }`
      : this.mechanicName;
  }

  // what was typed stays when the list is chosen instead, so + Vehicle finds it again
  toggleNewVehicle () : void {
    this.vehicleId = this.isNewVehicle
      ? null
      : NEW_VEHICLE_ID;

    this.clearError();
  }

  useSuggestion () : void {
    this.startMinutes = this.suggestedStart;

    this.save();
  }

  get vehicleSummary () : string {
    if ( this.isLoadingVehicles ) {
      return this.translateService.instant('BOOKING_SPACE.DIARY.LOADING_VEHICLES');
    }

    if ( !this.vehicles.length ) {
      return '';
    }

    return this.vehicles.length === 1
      ? this.translateService.instant('BOOKING_SPACE.DIARY.VEHICLE_ON_FILE')
      : this.translateService.instant('BOOKING_SPACE.DIARY.VEHICLES_ON_FILE', { count: this.vehicles.length });
  }

  // built once anything typed in has been created, so it names a saved customer and vehicle
  private buildDraft () : BookingDiaryBookingDraftModel {
    return {
      customer    : this.customer,
      date        : this.date,
      description : ( this.description || '' ).trim(),
      hours       : this.hours,
      mechanicId  : this.mechanicId || null,
      startMinutes: this.startMinutes,
      vehicle     : this.vehicles.find(vehicle => vehicle.id === this.vehicleId) || null
    };
  }

  // the empty choice names the state it stands for, and typing a new vehicle in stays the last row
  private buildVehicleOptions () : void {
    if ( !this.hasCustomerContext ) {
      this.vehicleOptions = [ {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.CHOOSE_CUSTOMER_FIRST'),
        value: null
      } ];

      return;
    }

    if ( this.isLoadingVehicles ) {
      this.vehicleOptions = [ {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.LOADING_VEHICLES'),
        value: null
      } ];

      return;
    }

    this.vehicleOptions = vehicleOptionsFor(this.vehicles, this.translateService);
  }

  // a customer or rego already on file is warned about, not refused, as the customer and vehicle screens do;
  // one made by an earlier refused save is no longer new, and a check that fails lets the booking through
  private confirmTypedRecords () : Promise<boolean> {
    const newVehicle = trimmedFields(this.newVehicle);
    const plate      = this.isNewVehicle && hasNewVehicleName(newVehicle)
      ? newVehicle.plate_number
      : '';

    return Promise.all([
      this.isNewCustomer
        ? this.bookingDiaryService.isCustomerOnFile(trimmedFields(this.newCustomer)).catch(() => false)
        : Promise.resolve(false),
      plate
        ? this.bookingDiaryService.isPlateNumberOnFile(plate).catch(() => false)
        : Promise.resolve(false)
    ])
      .then(([ isCustomerOnFile, isPlateOnFile ]) => {
        const key = onFileMessageKey(isCustomerOnFile, isPlateOnFile);

        return key
          ? this.confirmService.generic(this.translateService.instant(key))
            .then(() => true, () => false)
          : true;
      });
  }

  // created once and then chosen, so saving again after a refusal does not make them twice; a new
  // vehicle is only written once it can be named, so an empty one is no vehicle, not a blank one
  private createTypedRecords () : Promise<void> {
    const newVehicle = trimmedFields(this.newVehicle);
    const customer   = this.isNewCustomer
      ? this.bookingDiaryService.createCustomer(trimmedFields(this.newCustomer))
        .then(created => this.useCreatedCustomer(created))
      : Promise.resolve(this.customer);

    return customer.then(owner => this.isNewVehicle && hasNewVehicleName(newVehicle)
      ? this.bookingDiaryService.createVehicle(owner.id, newVehicle)
        .then(created => this.useCreatedVehicle(created))
      : null);
  }

  // a refused field reads as the server words it, such as "Mobile is not a number"
  private errorMessage ( err : any ) : string {
    if ( typeof err === 'string' ) {
      return err;
    }

    const fieldErrors : FieldErrorsModel = err && this.messagesService.isFieldError(err)
      ? err.field_errors || {}
      : {};
    const field       : string           = Object.keys(fieldErrors).find(key => ( fieldErrors[key] || [] ).length);

    if ( field ) {
      return this.properCasePipe.transform(field === 'base'
        ? fieldErrors[field][0]
        : `${ field.replace(/_/g, ' ') } ${ fieldErrors[field][0] }`, { sentence: true });
    }

    return ( err && err.message )
      || this.translateService.instant('BOOKING_SPACE.DIARY.SAVE_FAILED');
  }

  // the diary may be stale; if the re-read fails, the check the diary already passed stands
  private isSlotStillFree () : Promise<boolean> {
    if ( !this.mechanicId ) {
      return Promise.resolve(true);
    }

    return this.bookingDiaryService.isSlotFree(this.date, this.mechanicId, this.startMinutes, this.endMinutes)
      .catch(() => true);
  }

  // a customer with one vehicle almost always means that vehicle, so it is chosen
  private loadVehicles ( customer : BookingDiaryCustomerModel ) : void {
    this.isLoadingVehicles = true;

    this.bookingDiaryService.getCustomerVehicles(customer.id)
      .then(vehicles => {
        if ( this.customer !== customer ) {
          return;
        }

        this.vehicles = vehicles || [];

        const pending = this.pendingVehicleId && this.vehicles.some(vehicle => vehicle.id === this.pendingVehicleId)
          ? this.pendingVehicleId
          : null;

        // + Vehicle pressed while the list loaded keeps the new vehicle open
        if ( !this.isNewVehicle ) {
          this.vehicleId = pending || ( this.vehicles.length === 1
            ? this.vehicles[0].id
            : null );
        }

        this.pendingVehicleId = null;
      })
      .catch(() => {
        this.vehicles = [];
      })
      .then(() => {
        this.isLoadingVehicles = false;

        this.buildVehicleOptions();
      });
  }

  // the next start today that fits before closing and clears the mechanic; later slots first
  private nextFreeStart () : number {
    const duration = this.durationMinutes;
    const latest   = this.shopClosesMinutes - duration;

    const candidates : Array<number> = [];

    for ( let start = this.startMinutes + SLOT_MINUTES; start <= latest; start += SLOT_MINUTES ) {
      candidates.push(start);
    }

    for ( let start = this.shopOpensMinutes; start < this.startMinutes && start <= latest; start += SLOT_MINUTES ) {
      candidates.push(start);
    }

    const found = candidates.find(start => !this.hasGridForDate
      || this.isSlotFree(this.mechanicId, start, start + duration));

    return found === undefined
      ? null
      : found;
  }

  // a start that would run past closing greys, as it does on the appointment card; the save still checks
  private refreshTimeOptions () : void {
    this.timeOptions = timeOptionsBetween(this.shopOpensMinutes, this.shopClosesMinutes).map(option => ( {
      ...option,
      isDisabled: this.durationMinutes > 0 && ( option.value as number ) + this.durationMinutes > this.shopClosesMinutes
    } ));
  }

  private useCreatedCustomer ( created : BookingDiaryCustomerModel ) : BookingDiaryCustomerModel {
    this.customer      = created;
    this.isNewCustomer = false;

    return created;
  }

  private useCreatedVehicle ( created : BookingDiaryVehicleModel ) : void {
    this.vehicles  = this.vehicles.concat(created);
    this.vehicleId = created.id;

    this.buildVehicleOptions();
  }
}
